import { isNil } from '#layers/base/public';
import { LISTING_PAGE_SIZE_OPTIONS } from '#layers/ui/constants/pagination';

export interface PaginationVisibilityInput {
	/** The page currently shown, 1-based. */
	page: number;
	pageSize: number;
	/** Total items count. Provide only if the total number of items is known. */
	totalItems?: number;
	/** The current maximum number of known items. */
	currentItems?: number;
	/**
	 * How many of the counted items live outside the paged result, such as unsaved rows a relation
	 * field appends to page one. They are counted but belong to no server page, so they never add a page.
	 */
	unpagedItems?: number;
	/** Defaults to {@link LISTING_PAGE_SIZE_OPTIONS}. */
	pageSizeOptions?: Array<number>;
	loading?: boolean;
}

/** Whether the page controls have more than one page to offer. */
export function hasMultiplePages(input: Omit<PaginationVisibilityInput, 'page' | 'pageSizeOptions'>): boolean {
	// Keep controls stable until the replacement page has loaded.
	if (input.loading) return true;

	const pageableCount = Math.max(0, (input.totalItems ?? input.currentItems ?? 0) - (input.unpagedItems ?? 0));

	// An unknown total needs one full page before a next page is worth offering. Assuming pages exist
	// leaves the next control permanently enabled, because `lastPage` cannot be derived without a
	// total, so the user can page past the end of a short list.
	if (isNil(input.totalItems)) return pageableCount >= input.pageSize;

	return pageableCount > input.pageSize;
}

/** Whether the page size selector has a size worth switching to. */
export function canChangePageSize(input: Omit<PaginationVisibilityInput, 'page' | 'loading'>): boolean {
	const pageSizeOptions = input.pageSizeOptions ?? LISTING_PAGE_SIZE_OPTIONS;

	if (pageSizeOptions.length === 0) return false;

	const knownCount = Math.max(0, (input.totalItems ?? input.currentItems ?? 0) - (input.unpagedItems ?? 0));

	// A full page with no total proves nothing about how much more there is, so a larger size may
	// still reveal more rows. Only a partial page exhausts the result and makes the loaded count a
	// sound stand-in for the total.
	if (isNil(input.totalItems) && knownCount >= input.pageSize) return true;

	// Comparing against the smallest option rather than the selected size is what keeps the selector
	// reachable after the user picks a size larger than the list.
	return knownCount > pageSizeOptions[0]!;
}

/**
 * Whether a pagination bar would render anything at all. Call sites that hide the bar must ask this
 * rather than compare against the selected page size: a larger page size collapses the page controls
 * but still leaves the size selector to render.
 */
export function hasPaginationContent(input: PaginationVisibilityInput): boolean {
	return hasMultiplePages(input) || canChangePageSize(input) || input.page > 1;
}
